type ApiFn<T = string | number> = (param: T) => string;

interface ApiEndpoints {
    getCustomObjectFields: ApiFn<string | number>;
    getObjectDetails: ApiFn<string>;
    getFilters: ApiFn<string | number>;
    storeView: ApiFn<void>;
    storeViewSaveAs: ApiFn<void>;
    getViewDetail: ApiFn<string | number>;
    updateView: ApiFn<string | number>;
    getCustomLayoutDetail: ApiFn<[string, string, string]>;
    getCustomLayoutCreate: ApiFn<[string, string]>;
    pageTemplate: ApiFn<string>;
    getCustomLayoutFormulas: ApiFn<[string, string, string]>;
    createCustomObjectRecord: ApiFn<[string, string]>;
    updateCustomObjectRecord: ApiFn<[string, string, string]>;
    deleteCustomObjectRecord: ApiFn<[string, string]>;
    getRelatedSection: ApiFn<void>;
    getObjectDetailById: ApiFn<string | number>;

    globalSearchOption: ApiFn<void>;
    globalSearchPlainResult: ApiFn<[string, string]>;
    globalSearchRawResult: ApiFn<[string, string]>;
    globalSearchActionOptions: ApiFn<[string, string[], string]>;
    getRecordTypeByObject: ApiFn<string | number>;
    getRecordTypesForRecord: ApiFn<[string, string]>;
    uploadAttachment: ApiFn<void>;
    getRelatedObjectDataForResourceAlignment: ApiFn<void>;
    storeResourceAlignment: ApiFn<void>;
    storeConfirmVendorInstructorDate: ApiFn<void>;
    assignReadinessActivities: ApiFn<void>;
    getObjectActionButton: ApiFn<void>;
    getChecklistWorkstepList: ApiFn<void>;
    ChecklistWorkstepAssign: ApiFn<void>;
    alignVendor: ApiFn<void>;
    deleteReadinessActivities: ApiFn<void>;
    createCVS: ApiFn<void>;
    createPoSow: ApiFn<void>;
    sendPoToVendor: ApiFn<void>;
    sendInvoiceToDatev: ApiFn<void>;
    submitForApproval: ApiFn<void>;
    regenerateReadinessActivities: ApiFn<void>;
    createSession: ApiFn<void>;
    generateLesson: ApiFn<void>;
    syncUserToMongo: ApiFn<void>;
    submitSurvey: ApiFn<[string, string, string]>;
    emailAuditSummarize: ApiFn<void>;
    caseAuditRun: ApiFn<string>;
    caseAuditHistory: ApiFn<void>;
    emailTemplateMergeFieldSettings: ApiFn<void>;
    previewEmailTemplate: ApiFn<void>;
    sendEmailTemplatePreview: ApiFn<void>;
}

export const apiEndpoints: ApiEndpoints = {
    getCustomObjectFields: (entity_id) => `/api/fields?entity_id=${entity_id}`,
    getObjectDetails: (objectName) => `/api/custom-objects/object-list?name=${objectName}&check=false`,
    getFilters: (entity_id) => `/api/custom-objects/preset-filters?entity_id=${entity_id}`,
    storeView: () => `/api/new-view/store`,
    storeViewSaveAs: () => `/api/new-view/save-as`,
    getViewDetail: (viewId) => `/api/new-view/edit?id=${viewId}`,
    updateView: (viewId) => `/api/new-view/update?id=${viewId}`,
    getCustomLayoutDetail: (params: [string, string, string]) =>
        `/api/custom-objects/layout/${params[0]}/${params[1]}/${params[2]}/edit/v2`,
    pageTemplate: (object) => `/api/custom-objects/${encodeURIComponent(object)}/page-template`,
    getCustomLayoutCreate: (params: [string, string]) =>
        `/api/custom-objects/layout/${params[0]}/${params[1]}/null/edit`,
    getCustomLayoutFormulas: (params: [string, string, string]) =>
        `/api/custom-objects/layout/${params[0]}/${params[1]}/${params[2]}/formulas`,
    createCustomObjectRecord: (params: [string, string]) => `/api/custom-object/${params[0]}/${params[1]}/create`,
    updateCustomObjectRecord: (params: [string, string, string]) =>
        `/api/custom-object/${params[0]}/${params[1]}/${params[2]}/update`,
    deleteCustomObjectRecord: (params: [string, string]) => `/api/custom-object/${params[0]}/${params[1]}/delete`,
    getRelatedSection: () => `/api/custom-object/layout/get-related-section`,
    getObjectDetailById: (object_id) => `/api/new-view/create?id=${object_id}`,

    globalSearchOption: () => `/api/global-search`,
    globalSearchPlainResult: (params: [string, string]) =>
        `/api/global-search/plain-result?entity=${encodeURIComponent(params[0])}&query=${encodeURIComponent(params[1])}`,
    globalSearchRawResult: (params: [string, string]) =>
        `/api/global-search/raw-result?entity=${encodeURIComponent(params[0])}&query=${encodeURIComponent(params[1])}`,
    globalSearchActionOptions: (params: [string, string[], string]) =>
        `/api/global-search/actions/${encodeURIComponent(params[0])}/options?query=${encodeURIComponent(params[2])}${params[1]
            .map((step) => `&path[]=${encodeURIComponent(step)}`)
            .join('')}`,
    getRecordTypeByObject: (objectApiName) => `/api/custom-objects/${objectApiName}/record-types`,
    getRecordTypesForRecord: (params) =>
        `/api/custom-objects/${params[0]}/record-types?record_id=${encodeURIComponent(params[1])}`,
    uploadAttachment: () => `/custom-objects/attachment/upload`,
    getRelatedObjectDataForResourceAlignment: () => `/api/resource-alignment/related-object-data`,
    storeResourceAlignment: () => `/api/resource-alignment/resource-alignments`,
    storeConfirmVendorInstructorDate: () => `/api/confirm-vendor-instructor-date/resource-alignments`,
    assignReadinessActivities: () => `/api/resource-alignment/readiness-activities/assign`,
    getObjectActionButton: () => `/api/quick-action`,
    // syncEmailMessage: () => '/custom-object/salesforce/sync/email-message',
    // refreshEmailMessages: () => '/custom-object/email-messages/refresh',
    getChecklistWorkstepList: () => `/api/worksteps`,
    ChecklistWorkstepAssign: () => `/api/worksteps/save`,
    alignVendor: () => `/api/lrf-button/align-vendor`,
    deleteReadinessActivities: () => `/api/readiness-activities/delete`,
    regenerateReadinessActivities: () => `/api/readiness-activities/regenerate`,
    createCVS: () => `/api/lrf-quotation-button/create-cvs`,
    createPoSow: () => `/api/lrf-quotation-button/create-po-sow`,
    sendPoToVendor: () => `/api/vendor-po/send-po-to-vendor`,
    sendInvoiceToDatev: () => `/api/vendor-invoice/send-to-datev`,
    submitForApproval: () => `/api/approval/submit-for-approval`,
    createSession: () => `/api/lrf-button/create-session`,
    generateLesson: () => `/api/lrf-button/generate-lesson`,
    syncUserToMongo: () => `/api/mongo/sync/user`,
    submitSurvey: (params: [string, string, string]) =>
        `/api/custom-objects/layout/${params[0]}/${params[1]}/${params[2]}/survey/submit`,

    // Email thread summarize
    emailAuditSummarize: () => `/email-audit/summarize`,

    // Case-level audits (FTR / Sentiment / AFR)
    caseAuditRun: (type) => `/case-audit/${type}`,
    caseAuditHistory: () => `/case-audit/history`,
    emailTemplateMergeFieldSettings: () => `/api/custom-objects/email-template/merge-field-settings`,
    previewEmailTemplate: () => `/api/email/template-preview`,
    sendEmailTemplatePreview: () => `/api/email/template-preview/send`,
};
